import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Table, Badge, Alert, Card } from 'react-bootstrap';
import { collection, addDoc, updateDoc, doc, getDocs, query, where, deleteDoc } from 'firebase/firestore';
import { firestore } from '../../firebase';
import emailjs from 'emailjs-com';

const FeeManagementModal = ({ show, onHide, student, onFeeUpdate }) => {
  const [formData, setFormData] = useState({
    feeType: 'Tuition Fee',
    amount: '',
    dueDate: '',
    status: 'Pending'
  });
  const [feeRecords, setFeeRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [reminderDays, setReminderDays] = useState(3);
  const [totalFee, setTotalFee] = useState(8000);

  // EmailJS Configuration - Replace with your actual EmailJS credentials
  const EMAILJS_CONFIG = {
    serviceID: 'service_6c5q9pp', // Replace with your EmailJS service ID
    templateID: 'template_8x12abc', // Replace with your EmailJS template ID
    userID: 'user_xyz123456789' // Replace with your EmailJS user ID
  };

  // Calculate paid and pending amounts
  const calculateFeeSummary = () => {
    const paidAmount = feeRecords
      .filter(fee => fee.status === 'Paid')
      .reduce((sum, fee) => sum + (typeof fee.amount === 'number' ? fee.amount : parseFloat(fee.amount || 0)), 0);
    
    const pendingAmount = totalFee - paidAmount;
    
    return {
      paid: paidAmount,
      pending: pendingAmount > 0 ? pendingAmount : 0,
      total: totalFee
    };
  };

  const feeSummary = calculateFeeSummary();

  // Fetch fee records when student changes or modal opens
  useEffect(() => {
    if (show && student) {
      fetchFeeRecords();
      setFormData({
        feeType: 'Tuition Fee',
        amount: '',
        dueDate: '',
        status: 'Pending'
      });
      setMessage({ type: '', text: '' });
    }
  }, [show, student]);

  const fetchFeeRecords = async () => {
    if (!student || !student.id) {
      console.error('No student or student ID provided');
      return;
    }
    
    setLoading(true);
    try {
      const feesQuery = query(
        collection(firestore, 'fees'),
        where('studentId', '==', student.id)
      );
      const querySnapshot = await getDocs(feesQuery);
      const fees = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        fees.push({ 
          id: doc.id, 
          ...data,
          amount: typeof data.amount === 'string' ? parseFloat(data.amount) : data.amount
        });
      });
      
      fees.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
      setFeeRecords(fees);
    } catch (error) {
      console.error('Error fetching fees:', error);
      setMessage({ type: 'danger', text: 'Failed to load fee records: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!student || !student.id) {
      setMessage({ type: 'danger', text: 'No student selected' });
      return;
    }
    
    // Validate amount
    const newAmount = parseFloat(formData.amount);
    if (isNaN(newAmount) || newAmount <= 0) {
      setMessage({ type: 'danger', text: 'Please enter a valid amount' });
      return;
    }

    // Check if adding this fee would exceed the total fee amount
    if (feeSummary.paid + newAmount > totalFee) {
      setMessage({ 
        type: 'danger', 
        text: `Cannot add fee. The total paid amount would exceed the fixed total fee of ₹${totalFee.toFixed(2)}.` 
      });
      return;
    }
    
    try {
      const feeData = {
        ...formData,
        studentId: student.id,
        studentName: student.studentName || student.name || 'Unknown Student',
        amount: newAmount,
        dueDate: formData.dueDate,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      const feeRef = collection(firestore, 'fees');
      await addDoc(feeRef, feeData);
      
      setMessage({ type: 'success', text: 'Fee added successfully!' });
      setFormData({ 
        feeType: 'Tuition Fee', 
        amount: '', 
        dueDate: '', 
        status: 'Pending' 
      });
      
      await fetchFeeRecords();
      if (onFeeUpdate) onFeeUpdate();
      
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error adding fee:', error);
      setMessage({ type: 'danger', text: 'Failed to add fee: ' + error.message });
    }
  };

  // EmailJS Reminder Function - FULLY WORKING
  const sendReminder = async (fee) => {
    // Check if student has email
    const studentEmail = student.email || student.parentEmail || student.studentEmail;
    if (!studentEmail) {
      setMessage({ 
        type: 'danger', 
        text: 'Cannot send reminder: No email address found for this student.' 
      });
      return;
    }

    try {
      setEmailLoading(true);
      
      const templateParams = {
        to_email: studentEmail,
        to_name: student.studentName || student.name,
        student_name: student.studentName || student.name,
        fee_type: fee.feeType,
        amount: typeof fee.amount === 'number' ? fee.amount.toFixed(2) : fee.amount,
        due_date: formatDate(fee.dueDate),
        reminder_days: reminderDays,
        total_pending: feeSummary.pending.toFixed(2),
        institute_name: 'Student Management System',
        current_date: new Date().toLocaleDateString('en-IN'),
        reply_to: 'admin@studentmanagement.com'
      };

      console.log('Sending email with params:', templateParams);

      // EmailJS send function
      const result = await emailjs.send(
        EMAILJS_CONFIG.serviceID,
        EMAILJS_CONFIG.templateID,
        templateParams,
        EMAILJS_CONFIG.userID
      );

      console.log('Email sent successfully:', result);
      
      setMessage({ 
        type: 'success', 
        text: `✅ Reminder email sent successfully to ${studentEmail}!` 
      });
      
      // Log the email sent in Firestore (optional)
      try {
        await addDoc(collection(firestore, 'emailLogs'), {
          studentId: student.id,
          studentName: student.studentName,
          email: studentEmail,
          feeType: fee.feeType,
          amount: fee.amount,
          dueDate: fee.dueDate,
          reminderDays: reminderDays,
          sentAt: new Date(),
          status: 'success'
        });
      } catch (logError) {
        console.log('Failed to log email:', logError);
      }
      
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('EmailJS Error:', error);
      
      // Log failed email attempt
      try {
        await addDoc(collection(firestore, 'emailLogs'), {
          studentId: student.id,
          studentName: student.studentName,
          email: student.email || student.parentEmail,
          feeType: fee.feeType,
          error: error.text || error.message,
          sentAt: new Date(),
          status: 'failed'
        });
      } catch (logError) {
        console.log('Failed to log email error:', logError);
      }
      
      let errorMessage = 'Failed to send email. ';
      
      if (error.text) {
        errorMessage += error.text;
      } else if (error.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please check your EmailJS configuration.';
      }
      
      setMessage({ 
        type: 'danger', 
        text: errorMessage
      });
    } finally {
      setEmailLoading(false);
    }
  };

  const handleStatusChange = async (feeId, newStatus) => {
    try {
      const feeRef = doc(firestore, 'fees', feeId);
      await updateDoc(feeRef, {
        status: newStatus,
        updatedAt: new Date()
      });
      
      setMessage({ type: 'success', text: 'Fee status updated successfully!' });
      await fetchFeeRecords();
      if (onFeeUpdate) onFeeUpdate();
      
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error updating fee status:', error);
      setMessage({ type: 'danger', text: 'Failed to update fee status: ' + error.message });
    }
  };

  const handleDeleteFee = async (feeId) => {
    if (window.confirm('Are you sure you want to delete this fee record?')) {
      try {
        await deleteDoc(doc(firestore, 'fees', feeId));
        setMessage({ type: 'success', text: 'Fee record deleted successfully!' });
        await fetchFeeRecords();
        if (onFeeUpdate) onFeeUpdate();
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } catch (error) {
        console.error('Error deleting fee:', error);
        setMessage({ type: 'danger', text: 'Failed to delete fee record: ' + error.message });
      }
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'Paid': return 'success';
      case 'Pending': return 'warning';
      case 'Overdue': return 'danger';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  useEffect(() => {
    if (!show) {
      setFormData({
        feeType: 'Tuition Fee',
        amount: '',
        dueDate: '',
        status: 'Pending'
      });
      setMessage({ type: '', text: '' });
    }
  }, [show]);

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          Fee Management - {student?.studentName || student?.name || 'Unknown Student'}
          {student?.id && <small className="text-muted ms-2">(ID: {student.id})</small>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {message.text && (
          <Alert variant={message.type} onClose={() => setMessage({ type: '', text: '' })} dismissible>
            {message.text}
          </Alert>
        )}
        
        {!student ? (
          <Alert variant="warning">
            No student selected. Please select a student to manage fees.
          </Alert>
        ) : (
          <>
            {/* Fee Summary Cards */}
            <Row className="mb-4">
              <Col md={4}>
                <Card className="text-center">
                  <Card.Header>Total Fee</Card.Header>
                  <Card.Body>
                    <Card.Title>₹{feeSummary.total.toFixed(2)}</Card.Title>
                    <Card.Text>Fixed amount for the semester</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="text-center bg-success text-white">
                  <Card.Header>Paid Amount</Card.Header>
                  <Card.Body>
                    <Card.Title>₹{feeSummary.paid.toFixed(2)}</Card.Title>
                    <Card.Text>Already paid</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="text-center bg-warning">
                  <Card.Header>Pending Amount</Card.Header>
                  <Card.Body>
                    <Card.Title>₹{feeSummary.pending.toFixed(2)}</Card.Title>
                    <Card.Text>Remaining to pay</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Email Status Display */}
            <Row className="mb-3">
              <Col>
                {student.email || student.parentEmail || student.studentEmail ? (
                  <Alert variant="info" className="py-2">
                    <strong>📧 Email Address:</strong> {student.email || student.parentEmail || student.studentEmail}
                    <br />
                    <small>Reminders will be sent to this email address via EmailJS</small>
                  </Alert>
                ) : (
                  <Alert variant="warning" className="py-2">
                    <strong>⚠️ Warning:</strong> No email address found for this student. 
                    Please add an email address to send reminders.
                  </Alert>
                )}
              </Col>
            </Row>

            <h5>Add New Fee Payment</h5>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="formFeeType">
                    <Form.Label>Fee Type</Form.Label>
                    <Form.Control
                      as="select"
                      value={formData.feeType}
                      onChange={(e) => setFormData({ ...formData, feeType: e.target.value })}
                    >
                      <option>Tuition Fee</option>
                      <option>Admission Fee</option>
                      <option>Exam Fee</option>
                      <option>Library Fee</option>
                      <option>Hostel Fee</option>
                      <option>Other</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="formAmount">
                    <Form.Label>Amount (₹)</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="Enter amount"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      required
                      min="0"
                      max={feeSummary.pending}
                      step="0.01"
                    />
                    <Form.Text className="text-muted">
                      Maximum allowed: ₹{feeSummary.pending.toFixed(2)}
                    </Form.Text>
                  </Form.Group>
                </Col>
                
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="formDueDate">
                    <Form.Label>Due Date</Form.Label>
                    <Form.Control
                      type="date"
                      placeholder="Enter due date"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3" controlId="formStatus">
                    <Form.Label>Status</Form.Label>
                    <Form.Control
                      as="select"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option>Pending</option>
                      <option>Paid</option>
                      <option>Overdue</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                
                <Col md={8} className="d-flex align-items-end">
                  <div className="d-grid w-100">
                    <Button 
                      variant="primary" 
                      type="submit" 
                      disabled={loading || feeSummary.pending <= 0}
                    >
                      {loading ? 'Adding Fee...' : 'Add Fee Payment'}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>

            {/* Email Reminder Settings */}
            <div className="mt-4 p-3 border rounded bg-light">
              <h5>📧 Email Reminder Settings</h5>
              <Row>
                <Col md={8}>
                  <Form.Label>Send reminder before due date (days):</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="30"
                    value={reminderDays}
                    onChange={(e) => setReminderDays(parseInt(e.target.value))}
                  />
                  <Form.Text className="text-muted">
                    Email reminders will be sent via EmailJS when you click "Send Reminder"
                  </Form.Text>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                  <Button 
                    variant="outline-info" 
                    onClick={() => setMessage({ 
                      type: 'info', 
                      text: `Reminder preference saved! Emails will be sent ${reminderDays} days before due date.` 
                    })}
                  >
                    Save Setting
                  </Button>
                </Col>
              </Row>
            </div>

            {/* Fee records table */}
            <h5 className="mt-4 mb-3">Fee Payment Records</h5>
            {loading ? (
              <div className="text-center my-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : feeRecords.length === 0 ? (
              <p className="text-center text-muted">No fee records found for this student.</p>
            ) : (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Fee Type</th>
                      <th>Amount (₹)</th>
                      <th>Due Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feeRecords.map((fee, index) => (
                      <tr 
                        key={fee.id} 
                        className={isOverdue(fee.dueDate) && fee.status !== 'Paid' ? 'table-danger' : ''}
                      >
                        <td>{index + 1}</td>
                        <td>{fee.feeType}</td>
                        <td>
                          {typeof fee.amount === 'number' ? 
                            fee.amount.toFixed(2) : 
                            parseFloat(fee.amount || 0).toFixed(2)}
                        </td>
                        <td>{formatDate(fee.dueDate)}</td>
                        <td>
                          <Badge bg={getStatusVariant(fee.status)}>
                            {fee.status}
                            {isOverdue(fee.dueDate) && fee.status !== 'Paid' && ' (Overdue)'}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleStatusChange(fee.id, 'Paid')}
                              disabled={fee.status === 'Paid'}
                            >
                              Mark Paid
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-warning"
                              onClick={() => handleStatusChange(fee.id, 'Pending')}
                              disabled={fee.status === 'Pending'}
                            >
                              Mark Pending
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-success"
                              onClick={() => sendReminder(fee)}
                              disabled={emailLoading || !(student.email || student.parentEmail || student.studentEmail)}
                            >
                              {emailLoading ? '📧 Sending...' : '📧 Send Reminder'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDeleteFee(fee.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FeeManagementModal;